import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { FieldErrors } from '../api/errors';
import type { PaymentMethodCode } from '../api/types';
import { captchaPlan } from '../domain/captcha';
import { stripRequiredMarker } from '../domain/labels';
import { selectHeading, selectParagraph, selectPaymentMethods, selectRecaptchaRequired, shouldShowError } from '../state/selectors';
import { useEnv, useFormState } from './context';
import { Field } from './fields/Field';
import { LockIcon } from './icons';
import { Recaptcha } from './Recaptcha';
import { Section } from './Section';

interface Props {
  errors: FieldErrors;
}

const METHOD_FIELD: Record<PaymentMethodCode, string> = { stripe: 'credit_card_payment', cash: 'cash_payment', check: 'check_payment', paypal: 'paypal_payment', fspay: 'credit_card_payment' };
const METHOD_FALLBACK: Record<PaymentMethodCode, string> = { stripe: 'Credit card', cash: 'Cash', check: 'Check', paypal: 'PayPal', fspay: 'Card' };

function CardField({ errors }: { errors: string[] }) {
  const { controller, stripe } = useEnv();
  const status = useSyncExternalStore(stripe.subscribe, stripe.status, stripe.status);
  const host = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'ready' || !host.current) return;
    const unmount = stripe.mountCard(host.current, ({ complete, error }) => {
      setLocalError(error);
      controller.send({ type: 'CARD_STATE', complete });
    });
    const el = host.current;
    const onFocus = () => setFocus(true);
    const onBlur = () => {
      setFocus(false);
      controller.send({ type: 'FIELD_BLURRED', key: 'card' });
    };
    el.addEventListener('focusin', onFocus);
    el.addEventListener('focusout', onBlur);
    return () => {
      unmount();
      el.removeEventListener('focusin', onFocus);
      el.removeEventListener('focusout', onBlur);
    };
  }, [status, stripe, controller]);

  const allErrors = Array.from(new Set([...(localError ? [localError] : []), ...errors]));
  return (
    <Field fieldKey="card" label="Card details" required errors={allErrors} group>
      {({ invalid }) => (
        <>
          {status === 'unavailable' ? (
            <p className="cbf-error" role="alert">
              Card payments are unavailable right now. Please try again later.
            </p>
          ) : (
            <div className={`cbf-stripe${focus ? ' is-focus' : ''}${invalid ? ' is-invalid' : ''}`} data-cbf-field="card">
              {status === 'ready' ? <div ref={host} /> : <span className="cbf-help">Loading secure card form…</span>}
            </div>
          )}
          <p className="cbf-secure">
            <LockIcon />
            <span>Secured by Stripe</span>
          </p>
        </>
      )}
    </Field>
  );
}

export function Payment({ errors }: Props) {
  const { controller, config } = useEnv();
  const state = useFormState();
  const methods = selectPaymentMethods(state);
  const form = state.bootstrap?.form ?? null;
  const label = (m: PaymentMethodCode) => stripRequiredMarker(form?.system_fields?.[METHOD_FIELD[m] as keyof NonNullable<typeof form>['system_fields']]?.label).text || METHOD_FALLBACK[m];
  const show = (key: string) => (shouldShowError(state, key, 2) ? Array.from(new Set([...(errors[key] ?? []), ...(state.apiErrors[key] ?? [])])) : []);
  const selected = state.values.paymentMethod;
  const plan = captchaPlan(selectRecaptchaRequired(state), config.recaptchaSiteKey, config.env);

  return (
    <Section heading={selectHeading(state, 'payment_section')} paragraph={selectParagraph(state, 'payment_information')}>
      {methods.length === 0 ? (
        <p className="cbf-error" role="alert">
          Online booking is not available right now. Please contact us to book.
        </p>
      ) : methods.length === 1 ? (
        <p className="cbf-label">{label(methods[0]!)}</p>
      ) : (
        <Field fieldKey="payment_method" label="Payment method" required errors={show('payment_method')} group>
          {() => (
            <div className="cbf-choice-grid cbf-choice-grid-2" role="radiogroup">
              {methods.map((m) => (
                <label key={m} className={`cbf-choice${selected === m ? ' is-on' : ''}`} htmlFor={`cbf-pay-${m}`}>
                  <input id={`cbf-pay-${m}`} type="radio" name="cbf-payment" checked={selected === m} onChange={() => controller.send({ type: 'PAYMENT_METHOD_SELECTED', method: m })} />
                  <span className="cbf-radio" aria-hidden="true" />
                  <span className="cbf-choice-body">
                    <span className="cbf-choice-title">
                      <span>{label(m)}</span>
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </Field>
      )}
      {selected === 'stripe' ? <CardField errors={show('card')} /> : null}
      {selected === 'cash' || selected === 'check' ? <p className="cbf-help">Please have payment ready at your appointment.</p> : null}
      {plan === 'token' || plan === 'blocked' ? <Recaptcha
          siteKey={config.recaptchaSiteKey}
          errors={show('recaptcha_token')}
          onToken={(token) => controller.send({ type: 'RECAPTCHA', token })}
        /> : null}
    </Section>
  );
}
